import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";
import "./AdminCampaigns.css";

const serverConfig = { withCredentials: true };
const BASE = config.apiUrl;

const ALL_TRIGGERS = ["ai_assist", "node_update", "todo_check", "habit_check"];

const TRIGGER_LABELS = {
  ai_assist:   "AI Assist",
  node_update: "Record Update",
  todo_check:  "Todo Checked",
  habit_check: "Habit Checked",
};

const STATUS_COLORS = {
  draft:  "#9ca3af",
  active: "#22c55e",
  ended:  "#6b7280",
};

const BLANK_QUESTION = () => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  text: "",
  type: "mcq",
  options: [""],
  order: 0,
});

// ── helpers ───────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => (
  <span className="acm-badge" style={{ background: STATUS_COLORS[status] }}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

const StarDist = ({ distribution, total }) => (
  <div className="acm-dist">
    {[5, 4, 3, 2, 1].map((n) => {
      const count = distribution[n] || 0;
      const pct = total > 0 ? (count / total) * 100 : 0;
      return (
        <div key={n} className="acm-dist-row">
          <span className="acm-dist-label">{n}★</span>
          <div className="acm-dist-bar-wrap">
            <div className="acm-dist-bar" style={{ width: `${pct}%` }} />
          </div>
          <span className="acm-dist-count">{count}</span>
        </div>
      );
    })}
  </div>
);

// ── Campaign Form ─────────────────────────────────────────────────────────

const CampaignForm = ({ initial, onSaved, onCancel }) => {
  const isEdit = !!initial?._id;
  const [name, setName] = useState(initial?.name || "");
  const [triggers, setTriggers] = useState(initial?.triggers?.length ? initial.triggers : ["ai_assist"]);
  const [minUsageCount, setMinUsageCount] = useState(initial?.minUsageCount ?? 3);
  const [questions, setQuestions] = useState(
    initial?.questions?.length
      ? initial.questions.map((q) => ({ ...q, type: q.type || "mcq", options: q.options?.length ? q.options : [""] }))
      : [BLANK_QUESTION()]
  );
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);

  const updateQuestion = (i, field, value) => {
    setQuestions((prev) => prev.map((q, idx) => idx === i ? { ...q, [field]: value } : q));
  };

  const updateOptions = (i, raw) => {
    const opts = raw.split("\n");
    updateQuestion(i, "options", opts);
  };

  const addQuestion = () => setQuestions((prev) => [...prev, { ...BLANK_QUESTION(), order: prev.length }]);
  const removeQuestion = (i) => setQuestions((prev) => prev.filter((_, idx) => idx !== i));

  const toggleTrigger = (t) => {
    setTriggers((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const buildPayload = () => ({
    name,
    triggers,
    minUsageCount: Number(minUsageCount),
    questions: questions
      .filter((q) => q.text.trim())
      .map((q, i) => ({
        ...q,
        order: i,
        options: q.type === "mcq" ? q.options.filter((o) => o.trim()) : [],
      })),
  });

  const save = async (andLaunch = false) => {
    if (!name.trim()) { setError("Campaign name is required."); return; }
    setSaving(true);
    if (andLaunch) setLaunching(true);
    setError(null);
    try {
      let campaign;
      if (isEdit) {
        const { data } = await axios.put(`${BASE}/api/internal/campaigns/${initial._id}`, buildPayload(), serverConfig);
        campaign = data.campaign;
      } else {
        const { data } = await axios.post(`${BASE}/api/internal/campaigns`, buildPayload(), serverConfig);
        campaign = data.campaign;
      }
      if (andLaunch) {
        const { data: ld } = await axios.post(`${BASE}/api/internal/campaigns/${campaign._id}/launch`, {}, serverConfig);
        campaign = ld.campaign;
      }
      onSaved(campaign);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
      setLaunching(false);
    }
  };

  return (
    <div className="acm-form-wrap">
      <h3 className="acm-form-title">{isEdit ? "Edit Campaign" : "New Campaign"}</h3>

      {error && <p className="acm-error">{error}</p>}

      <div className="acm-field">
        <label className="acm-label">Campaign name</label>
        <input className="acm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. April AI Feedback" />
      </div>

      <div className="acm-row">
        <div className="acm-field">
          <label className="acm-label">Triggers</label>
          <div className="acm-trigger-checks">
            {ALL_TRIGGERS.map((t) => (
              <label key={t} className="acm-check-label">
                <input
                  type="checkbox"
                  checked={triggers.includes(t)}
                  onChange={() => toggleTrigger(t)}
                />
                {TRIGGER_LABELS[t]}
              </label>
            ))}
          </div>
        </div>
        <div className="acm-field">
          <label className="acm-label">Min usage before showing</label>
          <input className="acm-input" type="number" min={0} value={minUsageCount} onChange={(e) => setMinUsageCount(e.target.value)} />
        </div>
      </div>

      <div className="acm-questions-section">
        <div className="acm-questions-header">
          <span className="acm-label">Questions</span>
          <button className="acm-btn-ghost" onClick={addQuestion}>+ Add question</button>
        </div>

        {questions.map((q, i) => (
          <div key={q.id} className="acm-question-card">
            <div className="acm-q-top">
              <span className="acm-q-num">{i + 1}</span>
              <input
                className="acm-input acm-q-text"
                placeholder="Question text"
                value={q.text}
                onChange={(e) => updateQuestion(i, "text", e.target.value)}
              />
              <button className="acm-btn-delete" onClick={() => removeQuestion(i)} title="Remove">×</button>
            </div>
            <div className="acm-q-type-row">
              <button
                className={`acm-type-tab ${q.type === "mcq" ? "active" : ""}`}
                onClick={() => updateQuestion(i, "type", "mcq")}
                type="button"
              >
                Multiple choice
              </button>
              <button
                className={`acm-type-tab ${q.type === "text" ? "active" : ""}`}
                onClick={() => updateQuestion(i, "type", "text")}
                type="button"
              >
                Text answer
              </button>
            </div>
            {q.type === "mcq" && (
              <>
                <textarea
                  className="acm-input acm-q-options"
                  placeholder={"Option A\nOption B\nOption C"}
                  rows={Math.max(3, q.options.length + 1)}
                  value={q.options.join("\n")}
                  onChange={(e) => updateOptions(i, e.target.value)}
                />
                <p className="acm-q-hint">One option per line</p>
              </>
            )}
            {q.type === "text" && (
              <p className="acm-q-hint">User will see a free-text input for this question.</p>
            )}
          </div>
        ))}
      </div>

      <div className="acm-form-footer">
        <button className="acm-btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="acm-btn-secondary" onClick={() => save(false)} disabled={saving}>
          {saving && !launching ? "Saving..." : "Save as draft"}
        </button>
        <button className="acm-btn-primary" onClick={() => save(true)} disabled={saving}>
          {launching ? "Launching..." : isEdit ? "Save & Launch" : "Create & Launch"}
        </button>
      </div>
    </div>
  );
};

// ── Campaign Detail ───────────────────────────────────────────────────────

const CampaignDetail = ({ campaignId, onBack, onStatusChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${BASE}/api/internal/campaigns/${campaignId}`, serverConfig)
      .then(({ data: d }) => { setData(d); setLoading(false); })
      .catch(() => { setError("Failed to load campaign."); setLoading(false); });
  };

  useEffect(() => { load(); }, [campaignId]);

  const act = async (action) => {
    setActing(true);
    setError(null);
    try {
      await axios.post(`${BASE}/api/internal/campaigns/${campaignId}/${action}`, {}, serverConfig);
      load();
      onStatusChange();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${action} campaign.`);
    } finally {
      setActing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this draft campaign?")) return;
    setActing(true);
    try {
      await axios.delete(`${BASE}/api/internal/campaigns/${campaignId}`, serverConfig);
      onBack();
      onStatusChange();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete.");
      setActing(false);
    }
  };

  if (loading) return <div className="acm-loading">Loading campaign...</div>;
  if (!data) return <div className="acm-error">{error || "Not found."}</div>;

  const { campaign, responses, stats } = data;

  if (editing) {
    return (
      <CampaignForm
        initial={campaign}
        onSaved={() => { setEditing(false); load(); onStatusChange(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="acm-detail">
      <button className="acm-back" onClick={onBack}>← All campaigns</button>

      <div className="acm-detail-header">
        <div>
          <h2 className="acm-detail-title">{campaign.name}</h2>
          <div className="acm-detail-meta">
            <StatusBadge status={campaign.status} />
            <span className="acm-meta-item">Triggers: {(campaign.triggers || []).map((t) => TRIGGER_LABELS[t]).join(", ") || "—"}</span>
            <span className="acm-meta-item">Min usage: {campaign.minUsageCount}</span>
            {campaign.startedAt && <span className="acm-meta-item">Started: {new Date(campaign.startedAt).toLocaleDateString()}</span>}
            {campaign.endedAt && <span className="acm-meta-item">Ended: {new Date(campaign.endedAt).toLocaleDateString()}</span>}
          </div>
        </div>
        <div className="acm-detail-actions">
          {campaign.status === "draft" && (
            <>
              <button className="acm-btn-primary" onClick={() => act("launch")} disabled={acting}>Launch</button>
              <button className="acm-btn-secondary" onClick={() => setEditing(true)}>Edit</button>
              <button className="acm-btn-danger" onClick={handleDelete} disabled={acting}>Delete</button>
            </>
          )}
          {campaign.status === "active" && (
            <button className="acm-btn-danger" onClick={() => act("end")} disabled={acting}>End Campaign</button>
          )}
        </div>
      </div>

      {error && <p className="acm-error">{error}</p>}

      {/* Stats */}
      <div className="acm-stats-row">
        <div className="acm-stat-box">
          <div className="acm-stat-val">{stats.total}</div>
          <div className="acm-stat-lbl">Responses</div>
        </div>
        <div className="acm-stat-box">
          <div className="acm-stat-val">{stats.avgRating ?? "—"}{stats.avgRating ? "★" : ""}</div>
          <div className="acm-stat-lbl">Avg Rating</div>
        </div>
        <div className="acm-stat-box acm-stat-dist">
          <StarDist distribution={stats.distribution} total={stats.total} />
        </div>
      </div>

      {/* Questions in this campaign */}
      {campaign.questions?.length > 0 && (
        <div className="acm-section">
          <p className="acm-section-label">Questions</p>
          {campaign.questions.map((q, i) => (
            <div key={q.id} className="acm-q-preview">
              <span className="acm-q-num">{i + 1}</span>
              <div>
                <p className="acm-q-preview-text">{q.text}</p>
                <p className="acm-q-preview-opts">{q.options.join(" · ")}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Responses */}
      <div className="acm-section">
        <p className="acm-section-label">Responses {stats.total > 50 ? "(showing latest 50)" : ""}</p>
        {responses.length === 0 ? (
          <p className="acm-empty">No responses yet.</p>
        ) : (
          <div className="acm-responses">
            {responses.map((r, i) => (
              <div key={i} className="acm-response-card">
                <div className="acm-response-top">
                  <span className="acm-response-stars">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                  <span className="acm-response-date">{new Date(r.createdAt).toLocaleDateString()}</span>
                </div>
                {r.answers?.length > 0 && (
                  <div className="acm-response-answers">
                    {r.answers.map((a, ai) => (
                      <div key={ai} className="acm-response-qa">
                        <span className="acm-response-q">{a.question}</span>
                        <span className="acm-response-a">{a.answer}</span>
                      </div>
                    ))}
                  </div>
                )}
                {r.comment && <p className="acm-response-comment">"{r.comment}"</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────

const AdminCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "detail" | "new"
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    axios.get(`${BASE}/api/internal/campaigns`, serverConfig)
      .then(({ data }) => { setCampaigns(data.campaigns); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sorted = [...campaigns].sort((a, b) => {
    const order = { active: 0, draft: 1, ended: 2 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  if (view === "new") {
    return (
      <div className="acm-page">
        <CampaignForm
          onSaved={(c) => { load(); setSelectedId(c._id); setView("detail"); }}
          onCancel={() => setView("list")}
        />
      </div>
    );
  }

  if (view === "detail" && selectedId) {
    return (
      <div className="acm-page">
        <CampaignDetail
          campaignId={selectedId}
          onBack={() => setView("list")}
          onStatusChange={load}
        />
      </div>
    );
  }

  return (
    <div className="acm-page">
      <div className="acm-list-header">
        <div>
          <h1 className="acm-title">Feedback Campaigns</h1>
          <p className="acm-subtitle">Create and run targeted feedback surveys. One campaign can be active per trigger at a time.</p>
        </div>
        <button className="acm-btn-primary" onClick={() => setView("new")}>+ New Campaign</button>
      </div>

      {loading ? (
        <p className="acm-loading">Loading...</p>
      ) : campaigns.length === 0 ? (
        <div className="acm-empty-state">
          <p>No campaigns yet.</p>
          <button className="acm-btn-primary" onClick={() => setView("new")}>Create your first campaign</button>
        </div>
      ) : (
        <div className="acm-table-wrap">
          <table className="acm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Min Usage</th>
                <th>Responses</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c._id} className="acm-table-row" onClick={() => { setSelectedId(c._id); setView("detail"); }}>
                  <td className="acm-table-name">{c.name}</td>
                  <td>{(c.triggers || []).map((t) => TRIGGER_LABELS[t]).join(", ") || "—"}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td>{c.minUsageCount}</td>
                  <td>{c.responseCount ?? 0}</td>
                  <td>{c.startedAt ? new Date(c.startedAt).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCampaigns;
